import { Sketch } from '@uiw/react-color'
import { PipetteIcon } from 'lucide-react'
import { useCallback, useState } from 'react'

const supportsEyeDropper = typeof window !== 'undefined' && 'EyeDropper' in window

export interface WidgetColorPickerProps {
	color: string
	displayColor?: string
	onChange: (color: string) => void
	colorPool?: Array<string>
	presets?: Array<{
		label: string
		value: string
		displayColor: string
	}>
}

export function WidgetColorPicker({ color, displayColor, onChange, colorPool, presets = [] }: WidgetColorPickerProps) {
	const [isPickingColor, setIsPickingColor] = useState(false)

	const handleEyeDropper = useCallback(async () => {
		if (!supportsEyeDropper) { return }
		setIsPickingColor(true)
		try {
			// @ts-expect-error -- EyeDropper API is not yet in all TS lib definitions
			const dropper = new EyeDropper()
			const result = await dropper.open()
			onChange(result.sRGBHex)
		} catch {
			// User cancelled or API unavailable
		} finally {
			setIsPickingColor(false)
		}
	}, [onChange])

	return (
		<div className="grid gap-1 mt-1">
			<div className="grid grid-cols-1">
				{presets.map((preset, index) => (
					<button
						key={index}
						className="flex items-center gap-2 hover:bg-grey-100 rounded-md p-1 cursor-pointer"
						onClick={() => onChange(preset.value)}
					>
						<div
							className="size-4 rounded relative"
							style={{
								boxShadow: 'inset 0 0 0 1px rgba(0, 0, 0, 0.1)',
								backgroundColor: preset.displayColor,
							}}
						/>
						<span className="text-xs text-grey-600">{preset.label}</span>
					</button>
				))}
			</div>
			{presets.length > 0 && <div className="h-[1px] bg-grey-100 mb-2" />}
			<div className="*:![box-shadow:none] [&>*>div:first-child]:!px-0 [&>*>div:first-child]:!pt-0">
				<Sketch
					color={displayColor ?? color}
					onChange={(color) => onChange(color.hex)}
					disableAlpha={true}
					presetColors={colorPool}
				/>
			</div>
			{supportsEyeDropper && (
				<button
					type="button"
					onClick={handleEyeDropper}
					disabled={isPickingColor}
					className="flex items-center gap-1.5 text-xs text-grey-500 hover:text-grey-800 transition-colors cursor-pointer px-1 py-1 rounded hover:bg-grey-100"
				>
					<PipetteIcon className="size-3.5" />
					{isPickingColor ? 'Picking...' : 'Pick from screen'}
				</button>
			)}
		</div>
	)
}
